import { useRef } from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { apiClient } from '../../../state';
import { usePersistImportRun } from './ImportRunsState';
import type { ImportResolvedTarget } from './TargetContext';

/**
 * Hook to return a function that will run an import.
 */
export function useRunImport() {
    const persistImportRun = usePersistImportRun();
    const targetCacheRef = useRef<Promise<ImportResolvedTarget> | null>(null);
    return useEventCallback(
        async (props: {
            trigger: analytics.ImportEventTrigger;
            resolveTarget: () => Promise<ImportResolvedTarget>;
            onStart?: (target: ImportResolvedTarget) => void | Promise<void>;
            source: api.ContentImportSource;
        }) => {
            const { trigger, resolveTarget, onStart, source } = props;
            await toast.catch(async () => {
                const resolveTargetPromise =
                    targetCacheRef.current ??
                    resolveTarget().catch((error) => {
                        // Clear cache if it fails, so we can retry
                        targetCacheRef.current = null;
                        throw error;
                    });
                targetCacheRef.current = resolveTargetPromise;
                const { site, space, changeRequestId, pageId } = await resolveTargetPromise;
                const target: api.ContentImportTarget = {
                    space: space.id,
                    changeRequest: changeRequestId,
                    page: pageId,
                };
                const sourceURL = source.type === 'website' ? source.url : null;
                if (target.changeRequest) {
                    analytics.logEvent(
                        analytics.createChangeRequestEvent(space, target.changeRequest, {
                            type: 'import_start',
                            source: source.type,
                            sourceURL,
                            trigger,
                        })
                    );
                } else {
                    analytics.logEvent(
                        analytics.createSpaceEvent(space, {
                            type: 'import_start',
                            source: source.type,
                            sourceURL,
                            trigger,
                        })
                    );
                }
                const result = await apiClient.imports.startImportRun(space.organization, {
                    source,
                    target,
                });
                persistImportRun({
                    origin: site ? { type: 'site', site: site.id } : null,
                    target,
                    id: result.data.id,
                });
                await onStart?.({ space, changeRequestId });
            }, 'Error while starting import');
        }
    );
}
